/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { TFunction } from 'i18next';

// Prompt
import invokePromptConfirm from '@features/Prompt/PromptConfirm';

// API
import {
  QuestionListItemFragment,
  TopicsQuery,
  UpdateTopicUserAssignmentsMutationFn,
} from '@app/types/blackroom-api';
import GET_TOPICS from '@features/Support/api/getTopics';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface InvokeWorkflowSuggestionParams {
  /** Topics assigned to the question(s) */
  topicNames: string[];
  topicIds: string[];
  /** IDs of newly assigned owners */
  newOwnerIds: string[];
  /** Current workspace ID */
  workspaceId: string;
  /** Current topic data from the topics query (to merge existing assigned users) */
  topicsData: TopicsQuery | undefined;
  /** The mutation function */
  updateTopicUserAssignments: UpdateTopicUserAssignmentsMutationFn;
  /** i18n translate function */
  t: TFunction;
}

interface SuggestWorkflowFromQuestionsParams {
  questions: QuestionListItemFragment[];
  currentOwnerIds: string[];
  previousOwnerIds: string[];
  workspaceId: string;
  topicsData: TopicsQuery | undefined;
  updateTopicUserAssignments: UpdateTopicUserAssignmentsMutationFn;
  t: TFunction;
}

/* -------------------------------------------------------------------------- */
/*                                  Function                                  */
/* -------------------------------------------------------------------------- */

function invokeWorkflowSuggestion({
  topicNames,
  topicIds,
  newOwnerIds,
  workspaceId,
  topicsData,
  updateTopicUserAssignments,
  t,
}: InvokeWorkflowSuggestionParams) {
  if (topicIds.length === 0 || newOwnerIds.length === 0) return;

  // Filter out owners already assigned to all relevant topics
  const ownersNotFullyAssigned = newOwnerIds.filter((ownerId) =>
    topicIds.some((topicId) => {
      const topic = topicsData?.topics.find((to) => to.id === topicId);
      const assignedUserIds = topic?.assignedUsers?.map((u) => u.id) ?? [];
      return !assignedUserIds.includes(ownerId);
    }),
  );

  if (ownersNotFullyAssigned.length === 0) return;

  const topicList = topicNames.join(', ');
  const userKey =
    ownersNotFullyAssigned.length === 1 ? 'singleUser' : 'multipleUsers';
  const topicKey = topicIds.length === 1 ? 'SingleTopic' : 'MultipleTopics';
  const message = t(`Support:workflows.suggestion.${userKey}${topicKey}`, {
    topics: topicList,
  }) as string;

  invokePromptConfirm({
    componentProps: {
      entityLabel: message,
      headless: true,
    },
    onConfirm: async () => {
      const assignments = topicIds.map((topicId) => {
        const existingTopic = topicsData?.topics.find(
          (topic) => topic.id === topicId,
        );
        const existingUserIds =
          existingTopic?.assignedUsers?.map((u) => u.id) ?? [];

        // Merge existing + new, deduplicate
        const mergedUserIds = [
          ...new Set([...existingUserIds, ...ownersNotFullyAssigned]),
        ];

        return { topicId, userIds: mergedUserIds };
      });

      await updateTopicUserAssignments({
        variables: {
          input: {
            workspaceId,
            assignments,
          },
        },
        refetchQueries: [GET_TOPICS],
      });
    },
  });
}

/* -------------------------------------------------------------------------- */
/*                          Suggest from questions                            */
/* -------------------------------------------------------------------------- */

export function suggestWorkflowFromQuestions({
  questions,
  currentOwnerIds,
  previousOwnerIds,
  workspaceId,
  topicsData,
  updateTopicUserAssignments,
  t,
}: SuggestWorkflowFromQuestionsParams) {
  const newOwnerIds = currentOwnerIds.filter(
    (id) => !previousOwnerIds.includes(id),
  );
  if (newOwnerIds.length === 0) return;

  const allTopics = questions.flatMap((q) => q.assignedTopics ?? []);
  const uniqueTopicsMap = new Map(allTopics.map((topic) => [topic.id, topic]));
  const uniqueTopics = [...uniqueTopicsMap.values()];

  if (uniqueTopics.length === 0) return;

  invokeWorkflowSuggestion({
    topicNames: uniqueTopics.map((topic) => topic.name),
    topicIds: uniqueTopics.map((topic) => topic.id),
    newOwnerIds,
    workspaceId,
    topicsData,
    updateTopicUserAssignments,
    t,
  });
}

export default invokeWorkflowSuggestion;
